/**
 * Subscribe to personal-channel publications and dispatch to typed handler callbacks, keyed
 * by event type — a lookup, not an if/else chain (BR-7, DC-2). Replaces `usePersonalEvents`.
 *
 * The handler map is typed over `DomainEvent` — the exact union `@resida/shared` compiles
 * with server-side (BR-12) — plus one extra, non-union key: `new_chat_message`. Chat keeps its
 * own direct Centrifugo publish (BR-19/IAM-BR-RT1); it is dispatched through the same keyed
 * lookup with no special case in the dispatch path.
 *
 * IMPORTANT: All handler callbacks must be wrapped in `useCallback` at call sites. The
 * dispatch itself always calls the CURRENT handler map (via a ref, updated every render), so a
 * stale closure can never be invoked — but an unstable callback still makes the handler body
 * itself harder to reason about (a new closure captured on every render) and defeats memoized
 * work inside it.
 */

import { useEffect, useRef } from 'react';
import type { DomainEvent, DomainEventType } from '@resida/shared';
import { useCentrifugoContext } from '../contexts/CentrifugoContext';

export interface NewChatMessageData {
  conversationId: string;
  participantType: 'MANAGER' | 'TENANT';
}

type DomainEventHandlers = {
  [K in DomainEventType]?: (event: Extract<DomainEvent, { type: K }>) => void;
};

/** Exported so callers that build a handler map programmatically (e.g. "invalidate on any
 *  bridged event") can type it without redeclaring this shape. */
export type DomainEventHandlerMap = DomainEventHandlers & {
  new_chat_message?: (data: NewChatMessageData) => void;
};

export function useDomainEvents(handlers: DomainEventHandlerMap): void {
  const { personalSubscription } = useCentrifugoContext();

  // Latest-ref pattern: the effect below subscribes exactly once per subscription instance and
  // always reads the CURRENT handlers off this ref, so callers don't need every handler key
  // named as an explicit effect dependency (the handler set is open-ended by design).
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!personalSubscription) return;

    const listener = (ctx: { data: unknown }) => {
      const data = ctx.data as { type?: string } & Record<string, unknown>;
      const type = data.type;
      if (!type) return;

      const handler = (handlersRef.current as Record<string, ((event: unknown) => void) | undefined>)[type];
      if (handler) {
        handler(data);
      }
    };

    personalSubscription.on('publication', listener);
    return () => {
      personalSubscription.off('publication', listener);
    };
  }, [personalSubscription]);
}
